import { createWorkshopRecordStore } from "@/lib/create-workshop-record-store";
import {
  RECOMMENDATION_STATUSES,
  RECOMMENDATION_STATUS_DEFAULT,
  type RecommendationStatus,
} from "@/lib/taxonomy";

/**
 * Recommendations register (Track A · REG-REC). A recommendation is a
 * proposed course of action with a written rationale (`body`). It moves
 * `draft → proposed → accepted` (or `rejected`). The `body` is the AI-draft
 * target: filled manually now, and by `AI-REC` later (which only ever fills
 * the draft — a human accepts/advances status). Built on
 * {@link createWorkshopRecordStore}.
 */

export type { RecommendationStatus };

export type Recommendation = {
  id: string;
  workshopId: string;
  title: string;
  /** The recommendation prose — the AI-REC draft target. */
  body: string;
  status: RecommendationStatus;
  /** Who owns the recommendation (display name). */
  owner: string;
  createdAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 seam). */
  tags?: string[];
};

export type RecommendationPatch = Partial<
  Pick<Recommendation, "title" | "body" | "status" | "owner" | "tags">
>;

export const useRecommendations = createWorkshopRecordStore<
  Recommendation,
  RecommendationPatch
>({
  idbKey: "xaes-recommendations",
  recordType: "recommendations",
  sanitiseItem: (r, fallbackWorkshopId) => {
    if (typeof r.id !== "string") return null;
    if (typeof r.title !== "string") return null;
    const status: RecommendationStatus = RECOMMENDATION_STATUSES.includes(
      r.status as RecommendationStatus,
    )
      ? (r.status as RecommendationStatus)
      : RECOMMENDATION_STATUS_DEFAULT;
    return {
      id: r.id,
      workshopId:
        typeof r.workshopId === "string" ? r.workshopId : fallbackWorkshopId,
      title: r.title,
      body: typeof r.body === "string" ? r.body : "",
      status,
      owner: typeof r.owner === "string" ? r.owner : "",
      createdAt: typeof r.createdAt === "number" ? r.createdAt : Date.now(),
      addedBy: typeof r.addedBy === "string" ? r.addedBy : "",
      tags: Array.isArray(r.tags)
        ? r.tags.filter((t): t is string => typeof t === "string")
        : undefined,
    };
  },
});
